// ============================================
// FILE: /frontend/src/components/listings/ChamaOptIn.jsx
// PURPOSE: Opt-in toggle for sellers to enable Group Buying
// SHOWS: When property meets chama eligibility criteria
// ============================================

import { useState, useEffect } from 'react';
import { Users, Info, Sparkles, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Eligible property sub-types for Chama/Group Buying
const CHAMA_ELIGIBLE_TYPES = [
  "off plan",
  "joint ventures",
  "commercial property for sale",
  "godowns and warehouses for sale"
];

const MIN_PRICE_FOR_CHAMA = 50000000; // 50 Million KES
const DEFAULT_TOTAL_SHARES = 100;
const FUNDING_PERIOD_MONTHS = 4;

export default function ChamaOptIn({ 
  category,
  type,
  subType, 
  price, 
  enabled, 
  onToggle,
  onChamaDataChange 
}) {
  const [isEligible, setIsEligible] = useState(false);

  // Check eligibility whenever inputs change
  useEffect(() => {
  const priceNum = parseFloat(price) || 0;
  const typeToCheck = (type || '').toLowerCase();
  const subTypeToCheck = (subType || '').toLowerCase();
  
  const isEligibleType = CHAMA_ELIGIBLE_TYPES.includes(typeToCheck);
  const isEligibleSubType = CHAMA_ELIGIBLE_TYPES.includes(subTypeToCheck);
  
  const eligible = 
    category === 'property' &&
    priceNum >= MIN_PRICE_FOR_CHAMA &&
    (isEligibleType || isEligibleSubType);
  
  console.log('[ChamaOptIn] Eligibility check:', { 
    category, type, subType, priceNum, isEligibleType, isEligibleSubType, eligible 
  });
  
  setIsEligible(eligible);

  if (!eligible && enabled) {
    onToggle(false);
  }
}, [category, type, subType, price, enabled, onToggle]);

  // Calculate chama data when enabled
  useEffect(() => {
    if (enabled && isEligible) {
      const priceNum = parseFloat(price) || 0;
      const totalShares = DEFAULT_TOTAL_SHARES;
      const sharePrice = priceNum / totalShares;
      
      // Funding deadline = 4 months from now
      const fundingDeadline = new Date();
      fundingDeadline.setMonth(fundingDeadline.getMonth() + FUNDING_PERIOD_MONTHS);

      onChamaDataChange({
        allowGroupBuy: true,
        totalShares,
        sharePrice,
        fundingDeadline: fundingDeadline.toISOString()
      });
    } else {
      onChamaDataChange({
        allowGroupBuy: false,
        totalShares: null,
        sharePrice: null,
        fundingDeadline: null
      });
    }
  }, [enabled, isEligible, price, onChamaDataChange]);

  // Don't render if not eligible
  if (!isEligible) {
    return null;
  }

  const priceNum = parseFloat(price) || 0;
  const sharePrice = priceNum / DEFAULT_TOTAL_SHARES;

  return (
    <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
      <CardContent className="p-3 sm:p-4">
        {/* Header with Toggle */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            <Label 
              htmlFor="chama-toggle" 
              className="text-xs sm:text-sm font-semibold text-purple-900 cursor-pointer"
            >
              Enable Group Buying (Chama)
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  <p>
                    Allow multiple buyers to co-own this property by purchasing shares. 
                    Buyers pay a 10% deposit to reserve shares, with full payment due 
                    within 90 days of 100% funding.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          
          <Switch
            id="chama-toggle"
            checked={enabled}
            onCheckedChange={onToggle}
          />
        </div>

        {/* Eligibility Badge */}
        <div className="flex items-center gap-2 mb-3">
          <Badge 
            variant="outline" 
            className="text-[10px] sm:text-xs bg-green-50 text-green-700 border-green-300"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            Eligible for Group Buying
          </Badge>
          <span className="text-[10px] sm:text-xs text-muted-foreground">
            {subType || type} • KES {priceNum.toLocaleString()}
          </span>
        </div>

        {/* Preview of Chama Details (when enabled) */}
        {enabled && (
          <div className="bg-white rounded-lg p-2 sm:p-3 border border-purple-200 space-y-2">
            <p className="text-[10px] sm:text-xs font-semibold text-purple-800 mb-2">
              📊 Group Buying Configuration:
            </p>
            
            <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-xs">
              <div>
                <span className="text-muted-foreground">Total Shares:</span>
                <span className="font-semibold ml-1">{DEFAULT_TOTAL_SHARES}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Price/Share:</span>
                <span className="font-semibold ml-1">
                  KES {sharePrice.toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Funding Period:</span>
                <span className="font-semibold ml-1">{FUNDING_PERIOD_MONTHS} months</span>
              </div>
              <div>
                <span className="text-muted-foreground">Min Deposit:</span>
                <span className="font-semibold ml-1">10% per share</span>
              </div>
            </div>

            <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-purple-100">
              <AlertCircle className="w-3 h-3 text-purple-600 flex-shrink-0 mt-0.5" />
              <p className="text-[9px] sm:text-[10px] text-purple-700">
                Buyers will see a "Group Buying Available" badge and can purchase 
                shares instead of the full price. You'll receive funds after 100% funding.
              </p>
            </div>
          </div>
        )}

        {/* Info when disabled */}
        {!enabled && (
          <p className="text-[10px] sm:text-xs text-muted-foreground italic">
            Toggle on to allow fractional ownership through share purchases.
          </p>
        )}
      </CardContent>
    </Card>
  );
}